/**
 * The author picker, pre-filled with whoever is signed in.
 *
 * The picker itself is unchanged: this renders the ordinary reference field by
 * handing the same property back to `PropertyFieldBinding` with its `Field`
 * removed, which is how `PropertyGroupField` renders its anchor too. All that
 * is added is a default and a sentence explaining where the default came from.
 *
 * It only ever writes into an empty field, and only on a new guide. Overwriting
 * an author on an existing guide would rewrite a byline every time somebody
 * else opened it, which is the opposite of what an author field is for.
 *
 * When no `Person` carries the signed-in account's `uid` or `email` the field
 * does nothing at all and the editor picks by hand. That is not a fallback, it
 * is the normal case for the team bylines the roadmap deliberately keeps.
 */

import React, { useEffect, useRef, useState } from "react";
import {
    FieldProps,
    PropertyFieldBinding,
    useAuthController,
    useDataSource
} from "@firecms/core";
import { Typography } from "@firecms/ui";
import type { Person } from "@zennopay/schema";

import { authorsCollection } from "../collections/supporting";
import { findPersonForAccount } from "../derive/people";

/**
 * The property as the built-in reference field renders it.
 *
 * `PropertyFieldBinding` only reaches for its default component when the
 * property carries no `Field`, so removing it is what hands the picker back
 * rather than recursing into this component.
 */
function withoutCustomField<T>(property: T): T {
    const { Field: _custom, ...rest } = property as T & { Field?: unknown };
    return rest as T;
}

interface Matched {
    id: string;
    name: string;
    /** Which key linked the record to the account, for the note underneath. */
    via: "uid" | "email";
}

/**
 * The author record belonging to the signed-in user, if there is one.
 *
 * One fetch of a collection that holds a handful of documents, on mount. The
 * gate panel loads the same collection separately and that is left alone: the
 * two run in different tabs, and sharing a cache between them would be a cache
 * to invalidate for no measurable gain.
 */
function useSignedInAuthor(): { match?: Matched; loaded: boolean } {
    const dataSource = useDataSource();
    const authController = useAuthController();
    const uid = authController.user?.uid;
    const email = authController.user?.email ?? undefined;

    const [state, setState] = useState<{ match?: Matched; loaded: boolean }>({ loaded: false });

    useEffect(() => {
        let cancelled = false;
        if (!uid && !email) {
            setState({ loaded: true });
            return undefined;
        }

        dataSource
            .fetchCollection<Person>({ path: "authors", collection: authorsCollection as any })
            .then((entities) => {
                if (cancelled) return;
                const people = entities.map((entity) => ({
                    id: entity.id,
                    name: entity.values.name,
                    uid: entity.values.uid,
                    email: entity.values.email
                }));
                const found = findPersonForAccount(people, { uid, email });
                setState({
                    loaded: true,
                    ...(found
                        ? {
                            match: {
                                id: found.id,
                                name: found.name,
                                via: found.uid && found.uid.trim() === uid ? "uid" : "email"
                            }
                        }
                        : {})
                });
            })
            .catch(() => {
                /* A reader who cannot list authors still gets the picker, which
                   fails the same way it always did. Nothing here is worth an
                   error banner on a field that is only offering a shortcut. */
                if (!cancelled) setState({ loaded: true });
            });

        return () => {
            cancelled = true;
        };
    }, [dataSource, uid, email]);

    return state;
}

export function AuthorRefField(props: FieldProps<string>) {
    const { property, propertyKey, value, setValue, context } = props;
    const { match, loaded } = useSignedInAuthor();

    /* True only for the write this field made. An editor who then picks someone
       else must not keep reading "defaulted to you". */
    const [defaulted, setDefaulted] = useState(false);
    const appliedRef = useRef(false);

    const isNew = context.status === "new";
    const empty = !value || value.trim() === "";

    useEffect(() => {
        if (appliedRef.current || !loaded || !match || !isNew || !empty || context.disabled) {
            return;
        }
        appliedRef.current = true;
        setDefaulted(true);
        /* Validated on write, so the "required" error on a field the CMS just
           filled in clears itself rather than waiting for a save attempt. */
        setValue(match.id, true);
    }, [loaded, match, isNew, empty, context.disabled, setValue]);

    return (
        <div className={"flex flex-col gap-1"}>
            <PropertyFieldBinding<string, any>
                propertyKey={propertyKey}
                property={withoutCustomField(property)}
                context={context}/>

            {defaulted && match && value === match.id && (
                <Typography variant={"caption"} className={"opacity-70"}>
                    Set to <strong>{match.name}</strong>, the author record linked to your
                    account {match.via === "uid" ? "by UID" : "by email address"}. Change it if
                    this guide is somebody else&apos;s, or a team byline.
                </Typography>
            )}

            {loaded && !match && isNew && (
                <Typography variant={"caption"} className={"opacity-70"}>
                    No author record is linked to your account, so this is not filled in for you.
                    An admin can link one by putting your UID or email address on the author in
                    People → Authors. Team bylines have no account and are always picked by hand.
                </Typography>
            )}
        </div>
    );
}
